import React, { useState, useEffect, useRef, useCallback } from 'react';
import Web3 from 'web3';
import { ethers } from 'ethers';
import { Button, message } from 'antd';
import './RevenueShareTab.css';
import CodebaseLicensingProcessorV2ABI from 'abis/NFTLicenseMarketplaceMultipleCoin';

// MinimalERC20ABI để lấy symbol và decimals của token
const MinimalERC20ABI = [
  {
    inputs: [],
    name: 'symbol',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'decimals',
    outputs: [{ internalType: 'uint8', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function',
  },
];

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

const RevenueShareTab = ({codeBaseDetail, chainId}) => {
  const [lpFile, setLpFile] = useState(null);
  const [daoFile, setDaoFile] = useState(null);
  const [account, setAccount] = useState(null);
  // State để lưu dữ liệu upload từ file (chỉ dùng để calculate, không hiển thị)
  const [uploadedLPHolders, setUploadedLPHolders] = useState(null);
  const [uploadedDAOMembers, setUploadedDAOMembers] = useState(null);
  // State để lưu claimers từ smart contract (hiển thị trên bảng)
  const [holderAddresses, setHolderAddresses] = useState([]);
  const [isClaimerList, setIsClaimerList] = useState(false);
  const [claimHistory, setClaimHistory] = useState([]);
  const [tokenSymbols, setTokenSymbols] = useState({});
  const [tokenDecimals, setTokenDecimals] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [isLoadingCalculations, setIsLoadingCalculations] = useState(false);
  const [isLoadingClaimHistory, setIsLoadingClaimHistory] = useState(false);

  // Ref để lưu event listeners và cleanup
  const accountsChangedListenerRef = useRef(null);

  // Helper function để khởi tạo Web3 instance an toàn
  const getWeb3Instance = useCallback(() => {
    if (typeof window.ethereum === 'undefined') {
      throw new Error('Ethereum object not found. Please install MetaMask.');
    }
    return new Web3(window.ethereum);
  }, []);

  // Helper functions để lấy token metadata
  const normalize = (a) => ((a || '').toLowerCase() === '0x0' ? ZERO_ADDRESS : (a || '').toLowerCase());

  const getNativeSymbol = (chainId) => {
    const id = Number(chainId);
    if (id === 56) return 'BNB';
    if (id === 137 || id === 80001) return 'MATIC';
    if (id === 7001) return 'ZETA';
    return 'ETH';
  };

  // Helper function để format amount từ wei với decimals cụ thể
  const formatFromWei = useCallback((amount, decimals = 18) => {
    if (!amount || amount === '0') return '0';
    try {
      const divisor = Web3.utils.toBN(10).pow(Web3.utils.toBN(decimals));
      const amountBN = Web3.utils.toBN(amount.toString());
      const formatted = amountBN.mul(Web3.utils.toBN(1e18)).div(divisor).toString();
      return Web3.utils.fromWei(formatted, 'ether');
    } catch (error) {
      console.error('Error formatting amount:', error);
      return '0';
    }
  }, []);

  const fetchTokenMeta = async (addr, chainId) => {
    const key = normalize(addr);
    if (key === ZERO_ADDRESS) {
      return { symbol: getNativeSymbol(chainId), decimals: 18 };
    }
    try {
      const web3 = getWeb3Instance();
      const token = new web3.eth.Contract(MinimalERC20ABI, key);
      let symbol = '';
      let decimals = 18;
      try {
        symbol = await token.methods.symbol().call();
      } catch (_) {}
      try {
        decimals = await token.methods.decimals().call();
      } catch (_) {}
      return { symbol, decimals: Number(decimals || 18) };
    } catch (error) {
      console.error('Error fetching token metadata:', error);
      return { symbol: '', decimals: 18 };
    }
  };

  // Setup event listeners cho accountsChanged với cleanup
  useEffect(() => {
    if (typeof window.ethereum === 'undefined') {
      return;
    }

    const handleAccountsChanged = (accounts) => {
      if (accounts && accounts.length > 0) {
        setAccount(accounts[0]);
      }
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    accountsChangedListenerRef.current = handleAccountsChanged;

    return () => {
      if (accountsChangedListenerRef.current && typeof window.ethereum !== 'undefined') {
        window.ethereum.removeListener('accountsChanged', accountsChangedListenerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    async function fetchDao() {
      const data = await Promise.all([
        connectToMetaMask()
      ]);
      let acountAddress = data[0];

      if (acountAddress && acountAddress.length > 0) {
        setAccount(acountAddress[0]);
      }
    }
    fetchDao();
  }, []);

  async function connectToMetaMask() {
    try {
      if (typeof window.ethereum === 'undefined') {
        throw new Error('MetaMask is not installed. Please install it to proceed.');
      }

      // Connect to MetaMask
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const ethereumProvider = new ethers.providers.Web3Provider(window.ethereum);

      // Get the signer's address
      const accounts = await ethereumProvider.listAccounts();
      const accountAddress = accounts && accounts.length > 0 ? accounts[0] : '';
      setAccount(accountAddress);
      return accounts;
    } catch (error) {
      console.error('Error connecting to MetaMask:', error);
      if (error.code === 4001) {
        message.warning('Please connect to MetaMask to continue.');
      } else {
        message.error('Failed to connect to MetaMask. Please try again.');
      }
      return [];
    }
  }

  const handleCalculate = async () => {
    setIsCalculating(true);
    try {
      if (typeof window.ethereum === 'undefined') {
        message.error('MetaMask is not installed. Please install it to proceed.');
        return false;
      }

      if (!account) {
        message.warning('Please connect your wallet first.');
        return false;
      }

      if (!codeBaseDetail?.nftMarketPlaceAddress) {
        message.error('NFT Marketplace Address is not available.');
        return false;
      }

      const web3 = getWeb3Instance();
      const networkId = await web3.eth.net.getId();

      if (parseFloat(networkId) !== parseFloat(chainId)) {
        message.warning('Wrong network. Please change your wallet network to Chain ID ' + chainId);
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(chainId) }],
          });
        } catch (switchError) {
          console.error('Error switching network:', switchError);
        }
        return false;
      }

      await calculateRevenueShare(codeBaseDetail.nftMarketPlaceAddress, account);
      return true;
    } catch (error) {
      console.error('Error in handleCalculateRevenueShare:', error);
      if (error.message?.includes('User rejected')) {
        message.warning('Transaction was rejected.');
      } else {
        message.error('An error occurred while calculating revenue shares. Please try again.');
      }
      return false;
    } finally {
      setIsCalculating(false);
    }
  };

  const calculateRevenueShare = async (ProcessorAddress, accountAddress) => {
    if (!ProcessorAddress || !accountAddress) {
      console.warn('ProcessorAddress or accountAddress is not provided.');
      return;
    }

    try {
      const web3 = getWeb3Instance();
      const CodebaseLicensingProcessorContract = new web3.eth.Contract(
        CodebaseLicensingProcessorV2ABI,
        ProcessorAddress,
      );

      // Lấy ra mảng chỉ chứa địa chỉ từ file upload
      const addressesOnlyLP = (uploadedLPHolders || []).map((holder) => holder.address.toLowerCase());
      const addressesOnlyDAO = (uploadedDAOMembers || []).map((holder) => holder.address.toLowerCase());

      if (addressesOnlyLP.length === 0 && addressesOnlyDAO.length === 0) {
        message.warning('Please upload LP or DAO holder files first.');
        return;
      }

      try {
        // Lấy gas price từ chain
        const gasPrice = await web3.eth.getGasPrice();

        // Tăng gas price lên 10%
        const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

        // Gửi giao dịch calculateRevenueShares
        await CodebaseLicensingProcessorContract.methods.calculateRevenueShares(addressesOnlyLP, addressesOnlyDAO).send({
          from: accountAddress,
          gasPrice: aggressiveGasPrice.toString(),
        });

        // Tải lại dữ liệu sau khi tính toán
        await handleLoadViewCalculations();
      } catch (error) {
        console.error('An error occurred while calculating revenue shares:', error);
        if (error.code === 4001) {
          message.warning('Transaction was rejected.');
        } else if (error.message?.includes('revert')) {
          message.error('Transaction failed. Please check the contract state.');
        } else {
          message.error('An error occurred while calculating revenue shares. Please try again.');
        }
        throw error;
      }
    } catch (error) {
      console.error('Error processing revenue share calculation:', error);
      throw error;
    }
  };

  const handleLoadViewCalculations = async () => {
    setIsLoadingCalculations(true);
    try {
      if (typeof window.ethereum === 'undefined') {
        message.error('MetaMask is not installed. Please install it to proceed.');
        return false;
      }

      if (!account) {
        message.warning('Please connect your wallet first.');
        return false;
      }

      if (!codeBaseDetail?.nftMarketPlaceAddress) {
        message.error('NFT Marketplace Address is not available.');
        return false;
      }

      const web3 = getWeb3Instance();
      const networkId = await web3.eth.net.getId();

      if (parseFloat(networkId) !== parseFloat(chainId)) {
        message.warning('Wrong network. Please change your wallet network to Chain ID ' + chainId);
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(chainId) }],
          });
        } catch (switchError) {
          console.error('Error switching network:', switchError);
        }
        return false;
      }

      await loadCalculation(codeBaseDetail.nftMarketPlaceAddress, account);
      return true;
    } catch (error) {
      console.error('Error in handleLoadViewCalculations:', error);
      message.error('Failed to load calculations. Please try again.');
      return false;
    } finally {
      setIsLoadingCalculations(false);
    }
  };

  const loadCalculation = async (ProcessorAddress, accountAddress) => {
    if (!ProcessorAddress || !accountAddress) {
      console.warn('ProcessorAddress or accountAddress is missing.');
      return;
    }

    try {
      const web3 = getWeb3Instance();
      const CodebaseLicensingProcessorContract = new web3.eth.Contract(
        CodebaseLicensingProcessorV2ABI,
        ProcessorAddress,
      );

      try {
        let result = await CodebaseLicensingProcessorContract.methods
          .viewAllrevenueCalculations()
          .call({ from: accountAddress });
        //Process result
        let lastCalculation;
        for (let i = result.length - 1; i >= 0; i--) {
          if (result[i].claimers.length > 0) {
            lastCalculation = result[i];
            break;
          }
        }

        if (lastCalculation) {
          setIsClaimerList(true);
          let claimers = lastCalculation.claimers;
          let tokenAddresses = new Set();

          // Gom nhóm theo (wallet, tokenAddress) và cộng dồn số tiền
          const aggregated = new Map();
          for (let i = 0; i < claimers.length; i++) {
            const walletKey = normalize(claimers[i].wallet);
            const tokenKey = normalize(claimers[i].tokenAddress || ZERO_ADDRESS);
            const mapKey = walletKey + '|' + tokenKey;

            // Thu thập các token addresses để fetch symbols
            if (claimers[i].tokenAddress) {
              tokenAddresses.add(claimers[i].tokenAddress);
            }

            const prev = aggregated.get(mapKey) || {
              address: Web3.utils.toChecksumAddress(claimers[i].wallet),
              tokenAddress: claimers[i].tokenAddress,
              withdrawableLPHolder: '0',
              withdrawableDevPartner: '0',
              withdrawableDAOMember: '0',
            };

            const addLP = Web3.utils.toBN(String(claimers[i].withdrawableLPHolder || '0'));
            const addDev = Web3.utils.toBN(String(claimers[i].withdrawableDevPartner || '0'));
            const addDAO = Web3.utils.toBN(String(claimers[i].withdrawableDAOMember || '0'));
            const curLP = Web3.utils.toBN(String(prev.withdrawableLPHolder || '0'));
            const curDev = Web3.utils.toBN(String(prev.withdrawableDevPartner || '0'));
            const curDAO = Web3.utils.toBN(String(prev.withdrawableDAOMember || '0'));

            prev.withdrawableLPHolder = curLP.add(addLP).toString();
            prev.withdrawableDevPartner = curDev.add(addDev).toString();
            prev.withdrawableDAOMember = curDAO.add(addDAO).toString();

            aggregated.set(mapKey, prev);
          }

          const newHolderAddresses = Array.from(aggregated.values());

          // Fetch token symbols và decimals cho tất cả unique token addresses
          const symbols = {};
          const decimals = {};
          for (const tokenAddr of tokenAddresses) {
            try {
              const meta = await fetchTokenMeta(tokenAddr, chainId);
              const key = normalize(tokenAddr);
              symbols[key] = meta.symbol;
              decimals[key] = meta.decimals;
            } catch (error) {
              console.error('Error fetching token metadata for', tokenAddr, error);
              const key = normalize(tokenAddr);
              symbols[key] = '';
              decimals[key] = 18;
            }
          }
          setTokenSymbols(symbols);
          setTokenDecimals(decimals);
          setHolderAddresses(newHolderAddresses);
        } else {
          setHolderAddresses([]);
          setIsClaimerList(true); // Always show CLAIMER LIST even when empty
        }
      } catch (error) {
        console.error('Error loading calculation:', error);
        message.error('Failed to load calculations. Please try again.');
        setHolderAddresses([]);
      }
    } catch (error) {
      console.error('Error in loadCalculation:', error);
      message.error('An error occurred while loading calculations.');
    }
  };

  const handleViewCalculations = async () => {
    await handleLoadViewCalculations();
  };

  const loadClaimHistory = async (ProcessorAddress, accountAddress) => {
    if (!ProcessorAddress || !accountAddress) {
      console.warn('ProcessorAddress or accountAddress is not provided.');
      return;
    }

    try {
      const web3 = getWeb3Instance();
      const CodebaseLicensingProcessorContract = new web3.eth.Contract(
        CodebaseLicensingProcessorV2ABI,
        ProcessorAddress,
      );

      try {
        const history = await CodebaseLicensingProcessorContract.methods
          .viewAllClaimTransactions()
          .call({ from: accountAddress });

        // Fetch token metadata trước để có decimals
        const historyTokenAddresses = new Set();
        history.forEach((item) => {
          if (item.tokenAddress && item.tokenAddress !== ZERO_ADDRESS) {
            historyTokenAddresses.add(item.tokenAddress);
          }
        });

        const historySymbols = {};
        const historyDecimals = {};
        for (const tokenAddr of historyTokenAddresses) {
          try {
            const meta = await fetchTokenMeta(tokenAddr, chainId);
            const key = normalize(tokenAddr);
            historySymbols[key] = meta.symbol;
            historyDecimals[key] = meta.decimals;
          } catch (error) {
            console.error('Error fetching token metadata for', tokenAddr, error);
            const key = normalize(tokenAddr);
            historySymbols[key] = '';
            historyDecimals[key] = 18;
          }
        }

        // Định dạng dữ liệu trả về với decimals chính xác
        const formattedHistory = history.map((item) => {
          const tokenKey = normalize(item.tokenAddress || ZERO_ADDRESS);
          const decimals = historyDecimals[tokenKey] || 18;

          return {
            key: history.indexOf(item),
            claimer: item.claimer ? Web3.utils.toChecksumAddress(item.claimer) : 'N/A',
            claimedAmountDevPartner: item.claimedAmountDevPartner
              ? formatFromWei(item.claimedAmountDevPartner.toString(), decimals)
              : '0',
            claimedAmountLPHolder: item.claimedAmountLPHolder
              ? formatFromWei(item.claimedAmountLPHolder.toString(), decimals)
              : '0',
            claimedAmountDAOMember: item.claimedAmountDAOMember
              ? formatFromWei(item.claimedAmountDAOMember.toString(), decimals)
              : '0',
            tokenAddress: item.tokenAddress ? Web3.utils.toChecksumAddress(item.tokenAddress) : 'N/A',
            timestamp: item.timestamp ? new Date(item.timestamp * 1000).toLocaleString() : 'N/A',
          };
        });

        // Cập nhật state với lịch sử claim và token metadata
        setClaimHistory(formattedHistory);
        setTokenSymbols((prev) => ({ ...prev, ...historySymbols }));
        setTokenDecimals((prev) => ({ ...prev, ...historyDecimals }));
      } catch (error) {
        console.error('Error processing claim history:', error);
        message.error('Failed to process claim history. Please try again.');
      }
    } catch (error) {
      console.error('Error in loadClaimHistory:', error);
      message.error('An error occurred while loading claim history.');
    }
  };

  const handleViewClaimHistory = async () => {
    setIsLoadingClaimHistory(true);
    try {
      if (typeof window.ethereum === 'undefined') {
        message.error('MetaMask is not installed. Please install it to proceed.');
        return;
      }

      if (!account) {
        message.warning('Please connect your wallet first.');
        return;
      }

      if (!codeBaseDetail?.nftMarketPlaceAddress) {
        message.error('NFT Marketplace Address is not available.');
        return;
      }

      const web3 = getWeb3Instance();
      const networkId = await web3.eth.net.getId();

      if (parseFloat(networkId) !== parseFloat(chainId)) {
        message.warning('Wrong network. Please change your wallet network to Chain ID ' + chainId);
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(chainId) }],
          });
        } catch (switchError) {
          console.error('Error switching network:', switchError);
        }
        return;
      }

      await loadClaimHistory(codeBaseDetail.nftMarketPlaceAddress, account);
    } catch (error) {
      console.error('Error fetching claim history:', error);
      message.error('Failed to fetch claim history. Please try again.');
    } finally {
      setIsLoadingClaimHistory(false);
    }
  };

  const onHandleChange = async (event) => {
    const { name, value } = event.target;
    if (name === 'holderFile') {
      try {
        const file = event.target.files[0];
        if (!file) return;

        // Cập nhật state để hiển thị tên file
        setLpFile(file.name);

        const fileUrl = URL.createObjectURL(file);
        const response = await fetch(fileUrl);
        const text = await response.text();
        URL.revokeObjectURL(fileUrl); // Cleanup

        const lines = text.split('\n').filter((line) => line.trim());
        const dataHolderAddress = [];

        lines.forEach((line, index) => {
          if (index === 0) return; // Skip header row
          
          const trimmedLine = line.trim();
          if (!trimmedLine) return;

          const parts = trimmedLine.split(',').map((part) => part.trim());
          if (parts.length === 0) return;

          // Remove all quotes using regex
          let address = parts[0].replace(/"/g, '').trim();
          
          // Validate address format
          if (address && Web3.utils.isAddress(address)) {
            dataHolderAddress.push({
              address: Web3.utils.toChecksumAddress(address),
              amount: parts[1] ? parseFloat(parts[1]) || 0 : 0,
              transactionId: '',
            });
          } else if (address) {
            console.warn('Invalid address format:', address);
          }
        });

        setUploadedLPHolders(dataHolderAddress);
      } catch (error) {
        console.error('Error parsing LP holder file:', error);
        message.error('Failed to parse LP holder file. Please check the file format.');
        setLpFile(null);
      }
    }
    if (name === 'daoHolderFile') {
      try {
        const file = event.target.files[0];
        if (!file) return;

        // Cập nhật state để hiển thị tên file
        setDaoFile(file.name);

        const fileUrl = URL.createObjectURL(file);
        const response = await fetch(fileUrl);
        const text = await response.text();
        URL.revokeObjectURL(fileUrl); // Cleanup

        const lines = text.split('\n').filter((line) => line.trim());
        const dataDaoHolderAddress = [];

        lines.forEach((line, index) => {
          if (index === 0) return; // Skip header row
          
          const trimmedLine = line.trim();
          if (!trimmedLine) return;

          const parts = trimmedLine.split(',').map((part) => part.trim());
          if (parts.length === 0) return;

          // Remove all quotes using regex
          let address = parts[0].replace(/"/g, '').trim();
          
          // Validate address format
          if (address && Web3.utils.isAddress(address)) {
            dataDaoHolderAddress.push({
              address: Web3.utils.toChecksumAddress(address),
            });
          } else if (address) {
            console.warn('Invalid address format:', address);
          }
        });

        setUploadedDAOMembers(dataDaoHolderAddress);
      } catch (error) {
        console.error('Error parsing DAO holder file:', error);
        message.error('Failed to parse DAO holder file. Please check the file format.');
        setDaoFile(null);
      }
    }
  };

  return (
    <div className="revenue-share-container">
      <div className="file-upload-section">
        <div className="label">LP File</div>
        <div className="upload-row">
          <input
            type="file"
            name="holderFile"
            accept=".csv"
            onChange={onHandleChange}
            style={{ display: 'none' }}
            id="lpFileInput"
          />
          <Button onClick={() => document.getElementById('lpFileInput').click()}>
            Choose File
          </Button>
          <span className="file-name">
            {lpFile || 'No file chosen'}
          </span>
        </div>
      </div>

      <div className="file-upload-section" style={{ marginTop: '20px' }}>
        <div className="label">DAO File</div>
        <div className="upload-row">
          <input
            type="file"
            name="daoHolderFile"
            accept=".csv"
            onChange={onHandleChange}
            style={{ display: 'none' }}
            id="daoFileInput"
          />
          <Button onClick={() => document.getElementById('daoFileInput').click()}>
            Choose File
          </Button>
          <span className="file-name">
            {daoFile || 'No file chosen'}
          </span>
        </div>
      </div>

      <div className="action-buttons">
        <Button 
          type="primary"
          onClick={handleCalculate}
          className="calculate-button"
          disabled={isCalculating || ((!uploadedLPHolders || uploadedLPHolders.length === 0) && (!uploadedDAOMembers || uploadedDAOMembers.length === 0))}
          loading={isCalculating}
        >
          {isCalculating ? 'Calculating...' : 'CALCULATE REVENUE SHARES'}
        </Button>
        <Button 
          type="primary"
          onClick={handleViewCalculations}
          className="view-button"
          disabled={isLoadingCalculations}
          loading={isLoadingCalculations}
        >
          {isLoadingCalculations ? 'Loading...' : 'VIEW CALCULATIONS'}
        </Button>
        <Button 
          type="primary"
          onClick={handleViewClaimHistory}
          className="claim-history-button"
          disabled={isLoadingClaimHistory || !codeBaseDetail?.nftMarketPlaceAddress}
          loading={isLoadingClaimHistory}
        >
          {isLoadingClaimHistory ? 'Loading...' : 'VIEW CLAIM HISTORY'}
        </Button>
      </div>

      <div className="lp-holder-section">
        <h2>CLAIMER LIST</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', background: '#23272f', color: '#00ffe0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0006' }}>
            <thead>
              <tr style={{ backgroundColor: '#181c22' }}>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Address</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Token</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Withdrawable LP Holder</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Withdrawable Dev Partner</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Withdrawable DAO Member</th>
              </tr>
            </thead>
            <tbody>
              {holderAddresses && holderAddresses.length > 0 ? (
                holderAddresses.map((item, idx) => (
                  <tr key={`${item.address}-${item.tokenAddress || 'native'}-${idx}`}
                    style={{ 
                      borderBottom: '1px solid #00ffe0', 
                      background: idx % 2 === 0 ? '#23272f' : '#181c22', 
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '12px', color: '#fff' }}>{item.address}</td>
                    <td style={{ padding: '12px', color: '#fff' }}>
                      {item.tokenAddress
                        ? tokenSymbols[normalize(item.tokenAddress)] ||
                          (item.tokenAddress === '0x0000000000000000000000000000000000000000'
                            ? getNativeSymbol(chainId)
                            : Web3.utils.toChecksumAddress(item.tokenAddress))
                        : getNativeSymbol(chainId)}
                    </td>
                    <td style={{ padding: '12px', color: '#fff' }}>
                      {item.withdrawableLPHolder
                        ? (() => {
                            const tokenKey = normalize(item.tokenAddress || ZERO_ADDRESS);
                            const decimals = tokenDecimals[tokenKey] || 18;
                            return formatFromWei(item.withdrawableLPHolder.toString(), decimals);
                          })()
                        : '0'}
                    </td>
                    <td style={{ padding: '12px', color: '#fff' }}>
                      {item.withdrawableDevPartner
                        ? (() => {
                            const tokenKey = normalize(item.tokenAddress || ZERO_ADDRESS);
                            const decimals = tokenDecimals[tokenKey] || 18;
                            return formatFromWei(item.withdrawableDevPartner.toString(), decimals);
                          })()
                        : '0'}
                    </td>
                    <td style={{ padding: '12px', color: '#fff' }}>
                      {item.withdrawableDAOMember
                        ? (() => {
                            const tokenKey = normalize(item.tokenAddress || ZERO_ADDRESS);
                            const decimals = tokenDecimals[tokenKey] || 18;
                            return formatFromWei(item.withdrawableDAOMember.toString(), decimals);
                          })()
                        : '0'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    No claimers found. Please upload LP and DAO files, then click "Calculate Revenue Shares" and "View Calculations" to see results.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="lp-holder-section" style={{ marginTop: '60px' }}>
        <h2>CLAIM HISTORY</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', background: '#23272f', color: '#00ffe0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0006' }}>
            <thead>
              <tr style={{ backgroundColor: '#181c22' }}>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Claimer</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Token</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Claimed Amount (Dev Partner)</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Claimed Amount (LP Holder)</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Claimed Amount (DAO Member)</th>
                <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {claimHistory.length > 0 ? (
                claimHistory.map((item, idx) => (
                  <tr key={item.key || idx}
                    style={{ 
                      borderBottom: '1px solid #00ffe0', 
                      background: idx % 2 === 0 ? '#23272f' : '#181c22', 
                      transition: 'background 0.2s'
                    }}
                  >
                    <td style={{ padding: '12px', color: '#fff' }}>{item.claimer}</td>
                    <td style={{ padding: '12px', color: '#fff' }}>
                      {item.tokenAddress && item.tokenAddress !== 'N/A'
                        ? tokenSymbols[normalize(item.tokenAddress)] ||
                          (item.tokenAddress === '0x0000000000000000000000000000000000000000'
                            ? getNativeSymbol(chainId)
                            : Web3.utils.toChecksumAddress(item.tokenAddress))
                        : getNativeSymbol(chainId)}
                    </td>
                    <td style={{ padding: '12px', color: '#fff' }}>{item.claimedAmountDevPartner}</td>
                    <td style={{ padding: '12px', color: '#fff' }}>{item.claimedAmountLPHolder}</td>
                    <td style={{ padding: '12px', color: '#fff' }}>{item.claimedAmountDAOMember || '0'}</td>
                    <td style={{ padding: '12px', color: '#fff' }}>{item.timestamp}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                    No claim history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RevenueShareTab;